<template>
  <AppChatMessage is-from-user class="pb-6">
    <template #message-author>
      <v-avatar
        class="profile-menu-item__initials"
        size="32"
      >
        <span class="font-weight-bold">
          {{ profile.initials }}
        </span>
      </v-avatar>
    </template>
    <AIAssistantText :text="item.prompt" />
  </AppChatMessage>
  <template v-if="item.errorCode">
    <AppChatMessage class="pb-6">
      <template #message-author>
        <app-icon-ai-assistant :width="32" :height="32" />
      </template>
      <div class="d-flex align-center">
        <app-material-icon
          name="error"
          class="text-red-darken-1 mr-2"
          :width="24"
          :height="24"
        />
        <span class="font-weight-bold">
          {{ errorMessage }}
        </span>
      </div>
    </AppChatMessage>
  </template>
  <AppChatMessage v-if="item.response" class="pb-6" is-ready :is-error="!!item.errorCode">
    <template #message-author>
      <app-icon-ai-assistant :class="{ 'hidden-icon': item.errorCode }" :width="32" :height="32" />
    </template>
    <AIAssistantText :text="item.response" />
    <div v-if="hasSources" class="my-4">
      <div class="d-flex justify-center align-center column-gap-4 font-weight-bold mb-3">
        <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.sources`) }}</span>
        <v-divider class="my-auto" color="#dfe3e8" opacity="1"/>
      </div>
      <div v-for="(source, index) in item.sources" :key="index" class="source-item mb-2">
        <a :href="source.link" target="_blank">
          {{ source.title }}
        </a>
      </div>
    </div>
    <template #message-footer>
      <div class="message-footer">
        <p class="text--x-small text--light d-flex items-center column-gap-1">
          <app-icon-poly-office color="#375576" size="16" />
          <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.disclaimer`) }}</span>
        </p>
        <AIAssistantMessagesItemActions
          class="d-flex justify-end"
          @copy="handleCopySummary"
          @downvote="handleFeedbackDownVote"
          @upvote="handleFeedbackUpVote"
        />
      </div>
    </template>
  </AppChatMessage>
  <app-button
    v-if="isRetryable"
    type="outlined"
    variant="neutral"
    class="mx-auto font-weight-bold"
    @click="$emit('retry')"
  >
    {{ $t(`${TRANSLATION_KEY_PREFIX}.retry`) }}
  </app-button>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'

import AIAssistantMessagesItemActions from '@/common/ai-assistant/AIAssistantMessagesItemActions.vue'
import AppChatMessage from '@/common/app-chat/AppChatMessage.vue'
import { DARK_BLUE_DARKEST } from '@/common/utils/colors'
import type { AIAssistantConversationMessage } from '@/types/ai-assistant'

import AIAssistantText from './AIAssistantText.vue'

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantMessages'

export default defineComponent({
  name: 'AIAssistantMessagesItem',
  emits: ['retry'],
  components: {
    AppChatMessage,
    AIAssistantText,
    AIAssistantMessagesItemActions,
  },
  props: {
    item: {
      type: Object as PropType<AIAssistantConversationMessage>,
      required: true,
    },
  },
  data() {
    return {
      TRANSLATION_KEY_PREFIX,
      DARK_BLUE_DARKEST,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('user', ['profile']),
    hasSources(): boolean {
      return !!this.item.sources && this.item.sources.length > 0
    },
    errorMessage(): string {
        return this.$t(`${TRANSLATION_KEY_PREFIX}.errorMessage`)
    },
    isRetryable(): boolean {
      const retryableErrors = ['NO_RESPONSE', 'STREAM_TIMEOUT', 'STREAM_INTERRUPTED', 'REQUEST_ERROR', 'NETWORK_ERROR']
      return retryableErrors.includes(this.item.errorCode || '')
    },
  },
  methods: {
    async handleCopySummary() {
      await navigator.clipboard.writeText(this.item.response)
    },
    async handleFeedbackUpVote() {
      // TODO: handle up vote with Pendo
    },
    async handleFeedbackDownVote() {
      // TODO: handle down vote with Pendo
    },
  },
})
</script>

<style scoped lang="scss">
.profile-menu-item {
  &__initials {
    color: white;
    background: v-bind(DARK_BLUE_DARKEST);
    font-family: 'Lato', sans-serif;
    font-size: 12px;
  }
}

.message-footer {
  border-top: 1px solid #dfe3e8;
  padding-top: 16px;
}

.source-item {
  a {
    color: rgb(var(--v-theme-darkblue-500));
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
.hidden-icon {
  visibility: hidden;
}

</style>
